import { gql } from '@apollo/client';
import IconExpand from '@apollo/icons/default/IconExpand.svg';
import IconInfo from '@apollo/icons/default/IconInfo.svg';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Link,
  Spinner,
} from '@apollo/orbit';
import { motion } from 'framer-motion';
import React from 'react';
import { Link as ReactRouterLink, useLocation } from 'react-router-dom';

import { LazyUrlModal } from 'src/components/common/LazyUrlModal';
import { NoData } from 'src/components/common/noData/NoData';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { LocalTimestamp } from 'src/components/timestamp/Timestamp';
import Config from 'src/lib/config';
import { format } from 'src/lib/format';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { DocsRouter } from 'src/lib/routers';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import { HistogramTrace } from '../histogramTraceMapper/HistogramTraceMapper';

import { DefinitionList } from './DefinitionList';
import {
  QueryTraceInspector,
  queryTraceInspectorTraceFragment,
} from './queryTraceInspector/QueryTraceInspector';

interface Props {
  trace: GraphQLTypes.QueryTraceSummaryCardFragment | null;
  traceLoading: boolean;
  tracePercentile: number | null;
  nextSlowerTrace: HistogramTrace | null;
  nextFasterTrace: HistogramTrace | null;
  serviceId: string;
  schemaTag: string;
}

export const queryTraceSummaryCardFragment = gql`
  fragment QueryTraceSummaryCardFragment on Trace {
    id
    agentVersion
    clientName
    clientVersion
    startTime
    durationMs
    cacheMaxAgeMs
    cacheScope
    signature
    root {
      ...QueryTraceSummaryCardNodeFragment
      descendants {
        ...QueryTraceSummaryCardNodeFragment
      }
    }
    ...QueryTraceInspectorTraceFragment
  }
  fragment QueryTraceSummaryCardNodeFragment on TraceNode {
    cacheScope
    errors {
      message
      timestamp
    }
  }
  ${queryTraceInspectorTraceFragment}
`;

export const QueryTraceSummaryCard = ({
  trace,
  traceLoading,
  tracePercentile,
  nextSlowerTrace,
  nextFasterTrace,
  serviceId,
  schemaTag,
}: Props) => {
  const location = useLocation();

  // ensures greater precision in percentile the closer we get to 100
  let percentileWithUnits: {
    value: string | number;
    unit: string;
  } = {
    value: '--',
    unit: '',
  };
  if (tracePercentile !== null && !isNaN(tracePercentile)) {
    let percentile = tracePercentile;
    const x = 100 - 100 * percentile;
    const y = x.toPrecision(2);
    percentile = 100 - parseFloat(y);
    percentileWithUnits = format.splitUnits(
      format.suffix(Math.abs(percentile)),
    );
  }

  const requestHeaderCount = trace?.http?.requestHeaders.length ?? 0;
  const responseHeaderCount = trace?.http?.responseHeaders.length ?? 0;
  const variableCount = trace?.variables.length ?? 0;
  const hasMetadata =
    requestHeaderCount > 0 || responseHeaderCount > 0 || variableCount > 0;

  return (
    <Card variant="unstyled" className="mt-8">
      <CardHeader>
        <CardTitle
          className="min-h-8" /* prevent layout shift when changing traces */
        >
          Trace Details
        </CardTitle>
        {!traceLoading && trace && (
          <Button
            variant="secondary"
            leftIcon={<IconExpand />}
            size="sm"
            as={ReactRouterLink}
            to={setOverlayVisibleLocation({
              location,
              overlayName: 'operation-details',
            })}
          >
            View Operation
          </Button>
        )}
      </CardHeader>
      <CardBody className="min-h-[250px] pt-0">
        {traceLoading ? (
          <div className="mt-28 flex w-full justify-center">
            <Spinner size="md" />
          </div>
        ) : trace ? (
          <>
            <section>
              <DefinitionList
                className="text-sm text-primary"
                list={[
                  {
                    key: 'id',
                    name: 'ID',
                    value: (
                      <span className="truncate rounded bg-secondary px-1 py-0.5 font-mono">
                        {trace.id}
                      </span>
                    ),
                  },
                  {
                    key: 'timestamp',
                    name: 'Timestamp',
                    value: <LocalTimestamp value={trace.startTime} />,
                  },
                  {
                    key: 'performance',
                    name: 'Performance',
                    value: (
                      <span>
                        {format.ms(trace.durationMs, 1)}
                        {' / '}
                        {percentileWithUnits.value}
                        {percentileWithUnits.unit} percentile
                      </span>
                    ),
                  },
                  {
                    key: 'client',
                    name: 'Client',
                    value:
                      !trace.clientName && !trace.clientVersion
                        ? 'unspecified'
                        : `${trace.clientName || 'unspecified'}${
                            trace.clientVersion
                              ? `@${trace.clientVersion}`
                              : ' (unspecified version)'
                          }`,
                  },
                  {
                    key: 'agent',
                    name: 'Reporting Agent',
                    value: (
                      <span>
                        {trace.agentVersion || 'unspecified reporting agent'}
                      </span>
                    ),
                  },
                  {
                    key: 'metadata',
                    name: 'Additional Metadata',
                    value: hasMetadata ? (
                      <>
                        {[
                          requestHeaderCount > 0 &&
                            `request headers (${requestHeaderCount})`,
                          responseHeaderCount > 0 &&
                            `response headers (${responseHeaderCount})`,
                          variableCount > 0 &&
                            `graphql variables (${variableCount})`,
                        ]
                          .filter((v) => !!v)
                          .join(', ')}
                        <Button
                          className="ml-2"
                          variant="secondary"
                          leftIcon={<IconExpand className="w-2" />}
                          size="xs"
                          as={ReactRouterLink}
                          to={setOverlayVisibleLocation({
                            location,
                            overlayName: Config.modals.queryTraceInspector,
                          })}
                        >
                          Inspect
                        </Button>
                      </>
                    ) : (
                      'none'
                    ),
                  },
                  {
                    key: 'caching',
                    name: (
                      <div className="inline-flex items-center">
                        Cache Policy
                        <Tooltip
                          interactive
                          label={
                            <p className="p-2 font-normal normal-case">
                              <Link
                                isExternal
                                to={DocsRouter.path('ApolloServerCaching')}
                              >
                                Learn more
                              </Link>{' '}
                              about caching with Apollo.
                            </p>
                          }
                        >
                          <IconInfo className="ml-2 text-secondary" />
                        </Tooltip>
                      </div>
                    ),
                    value: trace.cacheMaxAgeMs ? (
                      <span>
                        max age: {format.ms(trace.cacheMaxAgeMs, 1)}, scope:{' '}
                        {trace.cacheScope
                          ? trace.cacheScope.toLowerCase()
                          : 'unspecified'}
                      </span>
                    ) : (
                      'none'
                    ),
                  },
                ]}
              />
            </section>

            <LazyUrlModal name={Config.modals.queryTraceInspector}>
              {({ isOpen, closeModal }) => (
                <motion.div
                  className="absolute z-50"
                  initial={{
                    opacity: 0,
                  }}
                  animate={{
                    opacity: 1,
                  }}
                  exit={{
                    opacity: 0,
                  }}
                  transition={{ duration: 0.2 }}
                >
                  <QueryTraceInspector
                    isOpen={isOpen}
                    serviceId={serviceId}
                    schemaTag={schemaTag}
                    onClose={closeModal}
                    trace={trace}
                  />
                </motion.div>
              )}
            </LazyUrlModal>
          </>
        ) : (
          <NoData
            className="mt-28 flex w-full justify-center"
            message="Trace not found"
            secondary={
              nextSlowerTrace || nextFasterTrace
                ? 'Try clicking a bar on the performance distribution.'
                : 'There are no recorded traces for this operation.'
            }
          />
        )}
      </CardBody>
    </Card>
  );
};
